import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MdClose, MdArrowBack } from 'react-icons/md';
import { API_BASE_URL } from './config';
import './ReportModal.css';

// Community guidelines report categories matching YouTube and backend database
const REPORT_CATEGORIES = [
  { id: 2, label: 'Sexual content' },
  { id: 3, label: 'Violent or repulsive content' },
  { id: 4, label: 'Hateful or abusive content' },
  { id: 5, label: 'Harassment or bullying' },
  { id: 6, label: 'Harmful or dangerous acts' },
  { id: 6, label: 'Suicide, self-harm or eating disorders' },
  { id: 7, label: 'Misinformation' },
  { id: 8, label: 'Child abuse' },
  { id: 9, label: 'Promotes terrorism' },
  { id: 10, label: 'Spam or misleading' },
  { id: 11, label: 'Legal issue', isLegal: true },
];

// Legal suboptions as seen in YouTube
const LEGAL_SUBOPTIONS = [
  'Copyright issue',
  'Privacy issue',
  'Trademark infringement',
  'Defamation',
  'Counterfeit',
  'Captions are missing (CVAA)',
];

function formatTime(totalSeconds) {
  const sec = Math.max(0, Math.floor(totalSeconds || 0));
  const minutes = Math.floor(sec / 60);
  const remainingSeconds = sec % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export default function ReportModal({
  isOpen,
  onClose,
  videoData,
  currentTime = 0,
  showToast,
  getApiHeaders,
}) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [step, setStep] = useState(1); // 1 = main categories, 2 = suboptions (legal) or manual textbox
  const [selectedLegalSuboption, setSelectedLegalSuboption] = useState('');
  const [manualDetail, setManualDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const onCloseRef = useRef(onClose);
  const shouldRenderRef = useRef(shouldRender);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    shouldRenderRef.current = shouldRender;
  }, [shouldRender]);

  // Smooth open / close animation lifecycle
  useEffect(() => {
    let timer = null;
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      setSelectedCategory(null);
      setStep(1);
      setSelectedLegalSuboption('');
      setManualDetail('');
      setIsSubmitting(false);
    } else if (shouldRenderRef.current) {
      setIsClosing(true);
      timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 200);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isOpen]);

  const handleSmoothClose = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsClosing(true);
    setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      if (onCloseRef.current) onCloseRef.current();
    }, 200);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && shouldRender) {
        handleSmoothClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shouldRender]);

  if (!shouldRender) return null;

  const isLegalSelected = selectedCategory?.isLegal;

  // Step 1 -> Step 2
  const handleNext = () => {
    if (!selectedCategory) return;
    setStep(2);
  };

  // Back to Step 1
  const handleBack = () => {
    setStep(1);
  };

  // Final Submit
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const videoId = videoData?.id || 157;
    const timestamp = formatTime(currentTime);
    const reasonId = selectedCategory?.id || 10;
    const additionalDetail = isLegalSelected
      ? `Legal subcategory: ${selectedLegalSuboption}`
      : manualDetail.trim();

    try {
      const headers = getApiHeaders ? getApiHeaders(true) : {
        devicetype: '0',
        token: 'c7e993808a344da9d59cfa20fa8b6061',
        request_type: 'USER',
        'Content-Type': 'application/json',
      };

      await fetch(`${API_BASE_URL}/api/v1/videos/${videoId}/report`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason_id: reasonId,
          timestamp,
          additional_detail: additionalDetail,
          check_video_description: false,
        }),
      });
    } catch (err) {
      console.warn('Report submission error (handled gracefully):', err);
    } finally {
      setIsSubmitting(false);
      handleSmoothClose();
      if (showToast) {
        showToast('Thank you for reporting. Our moderation team will review this video.');
      }
    }
  };

  return (
    <div
      className={`yt-report-modal-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleSmoothClose}
    >
      <div
        className={`yt-report-modal-container ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="yt-report-header">
          {step === 2 ? (
            <div className="yt-report-header-left">
              <button
                className="yt-report-back-btn"
                onClick={handleBack}
                aria-label="Back to main categories"
              >
                <MdArrowBack size={18} />
              </button>
              <h2 className="yt-report-title">Report</h2>
            </div>
          ) : (
            <h2 className="yt-report-title">Report</h2>
          )}
          <button
            className="yt-report-close-btn"
            onClick={handleSmoothClose}
            aria-label="Close"
          >
            <MdClose size={22} />
          </button>
        </div>

        {/* STEP 1: Main Category Selection */}
        {step === 1 && (
          <div className="yt-report-step-content">
            <div className="yt-report-subhead">
              <h3 className="yt-report-question">What's going on?</h3>
              <p className="yt-report-desc">
                We'll check for all community guidelines, so don't worry about making the perfect choice.
              </p>
            </div>

            <div className="yt-report-options-list">
              {REPORT_CATEGORIES.map((cat, idx) => {
                const isSelected = selectedCategory?.label === cat.label;
                return (
                  <label
                    key={`${cat.label}-${idx}`}
                    className={`yt-report-option-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    <div className={`yt-report-radio-ring ${isSelected ? 'checked' : ''}`}>
                      {isSelected && <div className="yt-report-radio-dot" />}
                    </div>
                    <span className="yt-report-option-text">{cat.label}</span>
                  </label>
                );
              })}
            </div>

            <div className="yt-report-footer">
              <button
                type="button"
                className={`yt-report-next-btn ${selectedCategory ? 'active' : 'disabled'}`}
                disabled={!selectedCategory}
                onClick={handleNext}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Legal Suboptions OR Manual Textbox */}
        {step === 2 && (
          <div className="yt-report-step-content">
            {isLegalSelected ? (
              /* Case A: Legal Issues Suboptions Selection */
              <>
                <div className="yt-report-subhead">
                  <h3 className="yt-report-question">Which legal issue?</h3>
                  <p className="yt-report-desc">
                    We take legal issues seriously, so we'll need to ask you for a bit more info.
                  </p>
                </div>

                <div className="yt-report-options-list">
                  {LEGAL_SUBOPTIONS.map((sub) => {
                    const isChecked = selectedLegalSuboption === sub;
                    return (
                      <label
                        key={sub}
                        className={`yt-report-option-item ${isChecked ? 'selected' : ''}`}
                        onClick={() => setSelectedLegalSuboption(sub)}
                      >
                        <div className={`yt-report-radio-ring ${isChecked ? 'checked' : ''}`}>
                          {isChecked && <div className="yt-report-radio-dot" />}
                        </div>
                        <span className="yt-report-option-text">{sub}</span>
                      </label>
                    );
                  })}
                </div>

                <div className="yt-report-footer">
                  <button
                    type="button"
                    className={`yt-report-next-btn ${selectedLegalSuboption ? 'active' : 'disabled'}`}
                    disabled={!selectedLegalSuboption || isSubmitting}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? 'Submitting...' : 'Next'}
                  </button>
                </div>
              </>
            ) : (
              /* Case B: Other Category with Manual Textbox Entry */
              <>
                <div className="yt-report-subhead">
                  <h3 className="yt-report-question">{selectedCategory?.label || 'Provide details'}</h3>
                  <p className="yt-report-desc">
                    Provide additional details to help our team understand the issue. Your report is completely confidential.
                  </p>
                </div>

                <div className="yt-report-textbox-wrap">
                  <div className="yt-report-timestamp-badge">
                    <span>Timestamp: {formatTime(currentTime)}</span>
                  </div>
                  <textarea
                    className="yt-report-textarea"
                    placeholder="Provide additional details to help our team understand the issue (optional)"
                    rows={4}
                    value={manualDetail}
                    onChange={(e) => setManualDetail(e.target.value)}
                    maxLength={500}
                    autoFocus
                  />
                  <div className="yt-report-char-count">
                    {manualDetail.length} / 500
                  </div>
                </div>

                <div className="yt-report-footer">
                  <button
                    type="button"
                    className="yt-report-next-btn active"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? 'Submitting...' : 'Report'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
